import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

// Check if current URL is a player-facing route (no admin-theme CSS vars available)
const isPlayRoute = () => {
  const path = window.location.pathname;
  return path.startsWith('/p/') || path.startsWith('/play/') || path.startsWith('/g/');
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    // Stale chunk after deployment — reload to fetch new assets
    if (error?.name === 'ChunkLoadError') {
      const reloaded = sessionStorage.getItem('chunk-reload');
      if (!reloaded) {
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
        return { hasError: false, error: null };
      }
      sessionStorage.removeItem('chunk-reload');
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleCopyError = () => {
    const errorText = this.state.error?.toString() || 'Unknown error';
    navigator.clipboard.writeText(errorText).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  renderPlayFallback() {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(to bottom, #F8FAFC, #F1F5F9)',
          fontFamily: '"Inter", sans-serif',
          minHeight: '100dvh',
          padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            padding: '2.5rem 2rem',
            margin: '0 1rem',
            maxWidth: '24rem',
            width: '100%',
            borderRadius: '1rem',
            background: '#FFFFFF',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div
            style={{
              width: '4rem',
              height: '4rem',
              margin: '0 auto 1.5rem',
              borderRadius: '9999px',
              background: 'rgba(229, 106, 84, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg style={{ width: '2rem', height: '2rem', color: '#E56A54' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', letterSpacing: '-0.025em', color: '#1E293B' }}>
            Something went wrong
          </h2>
          <p style={{ fontSize: '0.875rem', color: '#64748B', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            The game ran into an issue. This usually fixes itself.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              width: '100%',
              padding: '0.625rem 1.25rem',
              borderRadius: '0.5rem',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: '#FFFFFF',
              background: '#E56A54',
              border: 'none',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      // Play routes don't have admin-theme CSS vars — use inline styles
      if (isPlayRoute()) {
        return this.renderPlayFallback();
      }

      return (
        <div className="admin-theme gch-page min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
          <div className="max-w-md w-full space-y-6">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-[var(--admin-accent-light)] flex items-center justify-center">
                <ExclamationTriangleIcon className="w-8 h-8 text-[var(--admin-accent)]" />
              </div>
            </div>

            {/* Copy */}
            <div className="space-y-2">
              <h1 className="text-xl font-semibold gch-text-primary">
                Something unexpected happened
              </h1>
              <p className="text-sm gch-text-secondary leading-relaxed">
                Don't worry - your data is safe. Try refreshing, or contact support if this keeps happening.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="btn-admin-primary btn-admin-md inline-flex items-center justify-center gap-2"
              >
                Refresh page
              </button>
              <a
                href="/studio/support"
                className="btn-admin-outline btn-admin-md inline-flex items-center justify-center gap-2 text-center"
              >
                Contact support
              </a>
            </div>

            {/* Copy error details */}
            <button
              type="button"
              onClick={this.handleCopyError}
              className="text-xs gch-text-muted hover:gch-text-secondary transition-colors"
            >
              {this.state.copied ? 'Copied to clipboard' : 'Copy error details'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
